import { formatCode } from '@pl/domain';
import type { DiscrepancyDto, SiteDto, TransferDetailDto, TransferDto } from '@pl/domain';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useDiscrepancies, useSiteMap, useSkuMap, useTransfer, useTransfers } from '../../lib/queries';
import { Bar, Chip, Empty, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useSiteScope } from '../useSiteScope';
import { siteWhen } from '../when';
import { ResolveActions, resolvedWords, sinceWords } from './discrepancyKit';
import s from './Transfers.module.css';

type Filter = 'all' | 'open' | 'closed';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
];
const LIMIT = 100;
const isOpen = (t: TransferDto) => t.status === 'draft' || t.status === 'in_transit';

/** Draft, In transit, Closed (or Closed · N missing while any box is unresolved), Canceled. */
export function transferChip(t: TransferDto) {
  if (t.status === 'draft') return <Chip>Draft</Chip>;
  if (t.status === 'in_transit')
    return (
      <Chip tone="ink">
        <Icon name="truck" size={12} />
        In transit
      </Chip>
    );
  if (t.status === 'cancelled') return <Chip>Canceled</Chip>;
  return t.totals.missing > 0 ? (
    <Chip tone="warn" dot>
      Closed · {t.totals.missing} missing
    </Chip>
  ) : (
    <Chip tone="ok" dot>
      Closed
    </Chip>
  );
}

/** "CA → MD", with the full names for a screen reader. */
function Route({ from, to }: { from: SiteDto | undefined; to: SiteDto | undefined }) {
  return (
    <span className={s.route} aria-label={`From ${from?.name ?? 'another site'} to ${to?.name ?? 'another site'}`}>
      <SiteTag site={from} />
      <Icon name="arrow-right" size={13} />
      <SiteTag site={to} />
    </span>
  );
}

/**
 * Every transfer between the company's sites, newest first, and the one chosen: where it is, what is
 * on it, and — once it closed short — the boxes that never arrived, for a lead to write off or keep
 * waiting for. Transfers are made and scanned on the phone; the office watches and resolves.
 */
export function Transfers() {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const { siteId } = useSiteScope();
  const [filter, setFilter] = useState<Filter>('all');
  const list = useTransfers({ siteId, limit: LIMIT });
  const siteById = useSiteMap();

  const all = list.data ?? [];
  const byFilter: Record<Filter, TransferDto[]> = {
    all,
    open: all.filter(isOpen),
    closed: all.filter((t) => t.status === 'closed'),
  };
  const rows = byFilter[filter];

  return (
    <div className={s.page}>
      <section className={`panel ${s.listPanel}`} aria-labelledby="transfers-h">
        <div className="panel-h">
          <h2 id="transfers-h">All transfers</h2>
          <span className="grow" />
          <div className={`seg ${s.seg}`} role="group" aria-label="Show">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" className={filter === f.key ? 'on' : undefined} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                {f.label}
                {f.key === 'open' && list.data && <span className="code">{byFilter.open.length}</span>}
              </button>
            ))}
          </div>
        </div>
        {list.isLoadingError ? (
          <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
            <ErrorNote error={list.error} />
            <button type="button" className="btn" onClick={() => void list.refetch()}>
              Try again
            </button>
          </div>
        ) : list.isPending ? (
          <div className="panel-b">
            <Spinner label="Loading transfers" />
          </div>
        ) : (
          <>
            <RefetchNote queries={[list]} className="panel-b" />
            <ul className={s.rows}>
              {rows.map((t) => {
                const from = siteById.get(t.fromSiteId);
                const to = siteById.get(t.toSiteId);
                return (
                  <li key={t.id} className={t.id === id ? s.sel : undefined}>
                    <Link to={{ pathname: `/office/transfers/${t.id}`, search: location.search }} className={s.rowLink} aria-current={t.id === id ? 'true' : undefined}>
                      <span className={s.id}>
                        <span className="mono">{t.code}</span>
                        <Route from={from} to={to} />
                      </span>
                      <span className={s.chip}>{transferChip(t)}</span>
                      <span className={s.meta}>{rowMeta(t, from, to)}</span>
                    </Link>
                  </li>
                );
              })}
              {rows.length === 0 && <li className={s.none}>No transfers here.</li>}
            </ul>
            <div className={s.listFoot}>
              {all.length >= LIMIT ? `The newest ${LIMIT} transfers` : `${all.length} transfer${all.length === 1 ? '' : 's'}`}
            </div>
          </>
        )}
      </section>

      <div className={s.rcol}>{id ? <TransferDetail id={id} /> : <Empty title="Choose a transfer" description="What is on it, and where it is, shows here." />}</div>
    </div>
  );
}

/** The line under a transfer in the list: its progress, and when it last moved. */
function rowMeta(t: TransferDto, from: SiteDto | undefined, to: SiteDto | undefined) {
  if (t.status === 'draft')
    return (
      <>
        <span className={s.n}>
          {t.totals.picked} box{t.totals.picked === 1 ? '' : 'es'}
        </span>
        {to && !to.live && <span>{to.shortCode} not live · boxes will wait in transit</span>}
      </>
    );
  if (t.status === 'cancelled') return <span>Canceled · created by {t.createdByName}</span>;
  const tz = (t.status === 'closed' ? to : from)?.timezone ?? 'UTC';
  return (
    <>
      <span className={s.bar}>
        <Bar value={t.totals.received} max={t.totals.sent} tone={t.status === 'closed' ? 'ok' : undefined} />
      </span>
      <span className={s.n}>
        {t.totals.received}/{t.totals.sent}
      </span>
      <span className="grow" />
      <span>{t.status === 'closed' && t.closedAt ? `Closed ${siteWhen(t.closedAt, tz)}` : t.sentAt ? `Sent ${siteWhen(t.sentAt, tz)}` : ''}</span>
    </>
  );
}

function TransferDetail({ id }: { id: string }) {
  const query = useTransfer(id);
  const siteById = useSiteMap();
  const skuById = useSkuMap();
  const t = query.data;
  // Only a closed transfer has boxes that never arrived.
  const discrepancies = useDiscrepancies({ sourceId: id, limit: 200 }, t?.status === 'closed');

  if (query.isLoadingError)
    return (
      <div className="panel">
        <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
          <ErrorNote error={query.error} />
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  if (!t)
    return (
      <div className="panel panel-b">
        <Spinner label="Loading the transfer" />
      </div>
    );

  const from = siteById.get(t.fromSiteId);
  const to = siteById.get(t.toSiteId);
  const toCode = to?.shortCode ?? 'destination';
  const draft = t.status === 'draft';
  const missing = discrepancies.data ?? [];

  return (
    <>
      <section className="panel" aria-label={`Transfer ${t.code}`}>
        <RefetchNote queries={[query, discrepancies]} className="panel-b" />
        <div className={s.dh}>
          <div className={s.t1}>
            <span className="mono">{t.code}</span>
            {transferChip(t)}
            <span className="grow" />
            <span className="sub">{statusHint(t, from, to)}</span>
          </div>
          <div className={s.rt}>
            <div className={s.end}>
              <span className={s.nm}>
                <SiteTag site={from} />
                {from?.name ?? 'Another site'}
              </span>
              <small>
                {t.sentAt ? (
                  <>
                    Sent <b>{siteWhen(t.sentAt, from?.timezone ?? 'UTC')}</b> by <b>{t.sentByName}</b>
                  </>
                ) : (
                  <>
                    Started by <b>{t.createdByName}</b>
                  </>
                )}
              </small>
            </div>
            <div className={s.lane} aria-hidden="true">
              <span className={s.truck}>
                <Icon name="truck" size={22} />
                {t.status === 'in_transit' && t.sentAt && <span>{sinceWords(t.sentAt)}</span>}
              </span>
            </div>
            <div className={`${s.end} ${s.endR}`}>
              <span className={s.nm}>
                {to?.name ?? 'Another site'}
                <SiteTag site={to} />
              </span>
              <small>{arrivalWords(t, to)}</small>
            </div>
          </div>
        </div>
        <div className={s.strip}>
          <div>
            <span className="metric-k">Received at {toCode}</span>
            <span className="metric-v num">
              {t.totals.received}
              <small> / {t.totals.sent}</small>
            </span>
          </div>
          <div>
            <span className="metric-k">Boxes</span>
            <span className="metric-v num">{draft ? t.totals.picked : t.totals.sent}</span>
          </div>
          <div>
            <span className="metric-k">SKUs</span>
            <span className="metric-v num">{t.lines.length}</span>
          </div>
          <div className={t.totals.missing > 0 ? s.stripBad : undefined}>
            <span className="metric-k">Missing</span>
            <span className="metric-v num">{t.totals.missing}</span>
          </div>
        </div>
        <table className={`t ${s.sk}`}>
          <thead>
            <tr>
              <th>SKU</th>
              {draft ? (
                <th className="r">Picked</th>
              ) : (
                <>
                  <th className="r">Sent</th>
                  <th className="r">Received at {toCode}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {t.lines.map((l) => (
              <tr key={l.skuId}>
                <td>
                  <SkuTag sku={skuById.get(l.skuId)} withCode />
                </td>
                {draft ? (
                  <td className="r num">{l.picked}</td>
                ) : (
                  <>
                    <td className="r num">{l.sent}</td>
                    <td>
                      <div className={`prog ${s.prog}`}>
                        <Bar value={l.received} max={l.sent} tone={l.received === l.sent ? 'ok' : undefined} />
                        <span>
                          {l.received}/{l.sent}
                        </span>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {t.lines.length === 0 && (
              <tr>
                <td colSpan={draft ? 2 : 3} className="muted">
                  Nothing on it yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {missing.length > 0 && <MissingCallout t={t} fromCode={from?.shortCode} toCode={toCode} rows={missing} />}
    </>
  );
}

/** Who acts on a transfer next, and where. */
function statusHint(t: TransferDto, from: SiteDto | undefined, to: SiteDto | undefined): string {
  if (t.status === 'draft') return `${from?.shortCode ?? 'The sending site'} scans boxes onto it from Scan › Transfer`;
  if (t.status === 'in_transit') return `${to?.shortCode ?? 'The receiving site'} scans boxes in from Scan › Transfer › Incoming`;
  return '';
}

function arrivalWords(t: TransferDto, to: SiteDto | undefined): string {
  if (t.status === 'draft') return 'Not sent yet';
  if (t.status === 'cancelled') return 'Canceled before it was sent';
  if (t.status === 'closed') return `Closed ${t.closedAt ? siteWhen(t.closedAt, to?.timezone ?? 'UTC') : ''} · ${t.totals.received} of ${t.totals.sent} received`;
  return t.totals.received === 0 ? 'Not started' : `${t.totals.received} of ${t.totals.sent} scanned in`;
}

function MissingCallout({ t, fromCode, toCode, rows }: { t: TransferDetailDto; fromCode: string | undefined; toCode: string; rows: DiscrepancyDto[] }) {
  const skuById = useSkuMap();
  const open = rows.filter((d) => d.status === 'open').length;
  return (
    <section className="callout" aria-label={`Missing from ${t.code}`}>
      <span className="ic">
        <Icon name="alert" />
      </span>
      <div>
        <h3 className={s.calloutH}>
          {t.code} closed with {rows.length} box{rows.length === 1 ? '' : 'es'} missing
          {open > 0 ? <Chip tone="warn">{open} open</Chip> : <Chip tone="ok">All resolved</Chip>}
        </h3>
        <p className={s.calloutP}>
          {fromCode ?? '?'} → {toCode} · {t.totals.received} of {t.totals.sent} received. A missing box stays in transit until a lead resolves it.
        </p>
        <ul className={s.missing}>
          {rows.map((d) => (
            <li key={d.id} className={s.box} role="group" aria-label={formatCode(d.box.code)}>
              <div className={s.boxTop}>
                <span className="mono">{formatCode(d.box.code)}</span>
                <SkuTag sku={skuById.get(d.box.skuId ?? '')} withCode />
                <span className="grow" />
                {d.status === 'open' ? (
                  <Chip>
                    <Icon name="truck" size={12} />
                    In transit
                  </Chip>
                ) : (
                  <span className={s.done}>{resolvedWords(d)}</span>
                )}
              </div>
              <ResolveActions d={d} />
            </li>
          ))}
        </ul>
        {open > 0 && <p className={s.calloutSub}>If one turns up, {toCode} can still scan it in.</p>}
      </div>
    </section>
  );
}

